import { SVGProps } from 'react';

import { Country } from '@xemplo/country-helper';
import { getFlagByCode } from '@xemplo/flags';
import { withLazyComponent } from '@xemplo/lazy-component';

import { RuntimeFlagMode } from '../phone-field.types';

/**
 * Returns a React component that renders an SVG flag sprite for the given country code.
 * The component uses an SVG `<use>` element referencing a predefined flag sprite identified by the code.
 *
 * @param code - The country code (e.g., from a Country enum) used to identify the flag sprite.
 * @returns A React functional component that renders the flag SVG.
 */
export function getFlagSpriteByCode(code: Country.Code) {
  return () => (
    <svg width="22" height="16">
      <use href={`#dawn-flag-${code}`} />
    </svg>
  );
}

/**
 * Creates a lazy-loaded flag component for a given country code.
 * @param code - The country code to retrieve the flag for.
 * @returns A function that renders the flag SVG component with a fixed width of 22 and height of 16.
 */
export function lazyFlagWithProps(code: Country.Code) {
  const LazyFlag = withLazyComponent<SVGProps<SVGSVGElement>>(getFlagByCode(code));
  return () => <LazyFlag width={22} height={16} />;
}

/**
 * Retrieves the appropriate country flag component or element based on the specified mode and country code.
 *
 * @param mode - The flag rendering mode, which determines how the flag is displayed (e.g., individual component, sprite, or none).
 * @param countryCode - The ISO country code (e.g., 'US', 'CA') used to identify the country for the flag.
 * @returns The flag component or element if mode is 'Individual' or 'Sprite', otherwise undefined for 'None' or default.
 */
export function getCountryFlag(mode: RuntimeFlagMode, countryCode: Country.Code) {
  switch (mode) {
    case RuntimeFlagMode.Individual:
      return lazyFlagWithProps(countryCode);
    case RuntimeFlagMode.Sprite:
      return getFlagSpriteByCode(countryCode);
    case RuntimeFlagMode.None:
    default:
      return undefined;
  }
}
